import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getOffersByProductId, getProductBySlug } from '../../utils/api';
import { getDisplayImageSrc } from '../PriceComparison/simpleProductPageShared';
import RetailerLogo from '../RetailerLogo';
import {
  AFFILIATE_PAGE_SOURCE,
  trackCompatibilityChecked,
  trackPricedAffiliateDealClick,
} from '../../utils/enhancedAnalytics';
import { usePriceSectionView } from '../../utils/usePriceSectionView';
import {
  filterValidComparisonOffers,
  formatOfferPrice,
  getComparableOfferPrice,
  getOfferLink,
  getOfferListPresentation,
  getRetailerNameFromOffer,
} from '../comparisons/comparisonOffers';
import CompatibilityResult from './CompatibilityResult';
import { evaluateCompatibilityResult } from './compatibilityResultViewModel';
import { buildConnectionFacts } from './compatibilityConnectionCopy';
import './CompatibilityPage.css';
import '../comparisons/ProductVsComparisonPage.css';

const INITIAL_SIDE = {
  loadingProduct: true,
  loadingOffers: false,
  product: null,
  productError: null,
  offers: [],
  offersError: null,
};

function createInitialSides() {
  return {
    display: { ...INITIAL_SIDE },
    livescope: { ...INITIAL_SIDE },
  };
}

async function loadProductSide(slug, setSides, sideKey, cancelled) {
  const patch = (partial) => {
    if (cancelled()) return;
    setSides((prev) => ({
      ...prev,
      [sideKey]: { ...prev[sideKey], ...partial },
    }));
  };

  let product;
  try {
    product = await getProductBySlug(slug);
  } catch (error) {
    patch({
      loadingProduct: false,
      loadingOffers: false,
      product: null,
      productError: error?.message || 'product_failed',
      offers: [],
      offersError: null,
    });
    return;
  }

  if (!product) {
    patch({
      loadingProduct: false,
      loadingOffers: false,
      product: null,
      productError: 'not_found',
      offers: [],
      offersError: null,
    });
    return;
  }

  patch({
    loadingProduct: false,
    loadingOffers: true,
    product,
    productError: null,
  });

  try {
    const result = await getOffersByProductId(product.id);
    patch({
      loadingOffers: false,
      offers: filterValidComparisonOffers(result?.data),
      offersError: null,
    });
  } catch (error) {
    patch({
      loadingOffers: false,
      offers: [],
      offersError: error?.message || 'offers_failed',
    });
  }
}

function HeroImage({ product, shortLabel }) {
  const src = getDisplayImageSrc(product?.image_url);
  if (!src) {
    return (
      <div className="ab-compat-page-hero-placeholder" data-testid={`image-placeholder-${shortLabel}`}>
        {shortLabel} image unavailable
      </div>
    );
  }
  return (
    <img
      className="ab-compat-page-hero-image"
      src={src}
      alt={product?.name || shortLabel}
      data-testid={`image-${shortLabel}`}
    />
  );
}

function OfferList({
  sideKey,
  shortLabel,
  emptyOffersMessage,
  commerceIntro,
  side,
  productSlug,
  config,
}) {
  if (side.loadingProduct || side.loadingOffers) {
    return (
      <p className="ab-compat-page-loading" data-testid={`offers-loading-${sideKey}`}>
        Loading current retailer offers…
      </p>
    );
  }

  if (side.offersError) {
    return (
      <p className="ab-compat-page-error" data-testid={`offers-error-${sideKey}`}>
        Current retailer offers could not be loaded for {shortLabel}. Compatibility above is still valid.
      </p>
    );
  }

  if (!side.offers.length) {
    return (
      <p className="ab-compat-page-empty" data-testid={`offers-empty-${sideKey}`}>
        {emptyOffersMessage}
      </p>
    );
  }

  return (
    <div className="ab-compare-offers" data-testid={`offers-${sideKey}`}>
      <p className="ab-compat-page-commerce-intro">{commerceIntro}</p>
      {side.offers.map((offer, index) => {
        const retailer = getRetailerNameFromOffer(offer);
        const href = getOfferLink(offer);
        const presentation = getOfferListPresentation(offer);
        return (
          <article
            key={`${retailer}-${href || index}`}
            className="ab-compare-offer"
            data-testid={`offer-${sideKey}-${index}`}
            data-offer-state={presentation.state}
          >
            <div className="ab-compare-offer-top">
              <div>
                <RetailerLogo name={retailer} />
                <p className="ab-compare-offer-retailer">{retailer}</p>
              </div>
              <p className="ab-compare-offer-price">{presentation.priceLabel || formatOfferPrice(offer)}</p>
            </div>
            {(presentation.meta || presentation.disclaimer) && (
              <p className="ab-compare-offer-meta">
                {presentation.meta}
                {presentation.meta && presentation.disclaimer ? ' · ' : null}
                {presentation.disclaimer}
              </p>
            )}
            {href ? (
              <a
                className="ab-compare-cta"
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`offer-cta-${sideKey}-${index}`}
                onClick={() => {
                  try {
                    trackPricedAffiliateDealClick({
                      retailer,
                      affiliateUrl: href,
                      salePrice: getComparableOfferPrice(offer),
                      pageSource: AFFILIATE_PAGE_SOURCE.COMPATIBILITY_PAGE,
                      productSlug,
                      compatibilityId: config.id,
                      compatibilityDisplaySlug: config.displaySlug,
                      compatibilityLivescopeSlug: config.livescopeSlug,
                      offerId: offer?.id || offer?.offer_id,
                      placement: 'compatibility_offers',
                    });
                  } catch {
                    // Analytics must never block outbound navigation.
                  }
                }}
              >
                Check price at {retailer}
              </a>
            ) : null}
          </article>
        );
      })}
    </div>
  );
}

export default function CompatibilityPage({ config }) {
  const [sides, setSides] = useState(createInitialSides);
  const checkedKeyRef = useRef(null);
  const offersRef = useRef(null);

  const evaluation = useMemo(() => {
    if (!config?.displaySlug || !config?.livescopeSlug) return null;
    return evaluateCompatibilityResult({
      selectedProductIds: [config.displaySlug, config.livescopeSlug],
      includeIceCompleteness: false,
    });
  }, [config]);

  const viewModel = evaluation?.viewModel || null;
  const connectionFacts = useMemo(() => buildConnectionFacts(viewModel), [viewModel]);

  useEffect(() => {
    if (config?.documentTitle) {
      document.title = config.documentTitle;
    }
  }, [config?.documentTitle]);

  useEffect(() => {
    if (!config?.displaySlug || !config?.livescopeSlug) return undefined;
    let cancelled = false;
    const isCancelled = () => cancelled;
    setSides(createInitialSides());
    loadProductSide(config.displaySlug, setSides, 'display', isCancelled);
    loadProductSide(config.livescopeSlug, setSides, 'livescope', isCancelled);
    return () => {
      cancelled = true;
    };
  }, [config]);

  useEffect(() => {
    if (!config || !viewModel?.resultKey) return;
    const key = `${config.id}:${config.displaySlug}:${config.livescopeSlug}:${viewModel.resultKey}`;
    if (checkedKeyRef.current === key) return;
    checkedKeyRef.current = key;
    trackCompatibilityChecked({
      displaySlug: config.displaySlug,
      livescopeSlug: config.livescopeSlug,
      result: viewModel.resultKey,
      verificationState: viewModel.analytics?.verificationState,
      addCount: viewModel.actions.add.length,
      replaceCount: viewModel.actions.replace.length,
      notUsedCount: viewModel.actions.notUsed.length,
      compatibilityId: config.id,
    });
  }, [config, viewModel]);

  const loadedOfferCount =
    (Array.isArray(sides.display.offers) ? sides.display.offers.length : 0) +
    (Array.isArray(sides.livescope.offers) ? sides.livescope.offers.length : 0);
  usePriceSectionView(
    offersRef,
    {
      pageSource: AFFILIATE_PAGE_SOURCE.COMPATIBILITY_PAGE,
      productSlug: config?.livescopeSlug,
      freshnessState: loadedOfferCount > 0 ? 'CURRENT' : 'NONE',
      retailerCount: loadedOfferCount,
      hasCurrentVerifiedPrice: loadedOfferCount > 0,
      placement: 'compatibility_offers',
    },
    Boolean(config?.id && viewModel?.resultKey) && loadedOfferCount > 0
  );

  if (!config) return null;

  const displaySide = sides.display;
  const livescopeSide = sides.livescope;

  return (
    <article
      className="ab-compat-page"
      data-testid="compatibility-page"
      data-compatibility-id={config.id}
      data-display-slug={config.displaySlug}
      data-livescope-slug={config.livescopeSlug}
    >
      <header className="ab-compat-page-hero">
        <p className="ab-compat-page-kicker">Angler Built compatibility</p>
        <h1 className="ab-compat-page-title">{config.question}</h1>
        <p className="ab-compat-page-framing">{config.framing}</p>
        <div className="ab-compat-page-hero-grid">
          <div
            className="ab-compat-page-hero-card"
            data-testid="compatibility-hero-display"
            data-canonical-slug={config.displaySlug}
          >
            <p className="ab-compat-page-hero-label">{config.displayShortLabel}</p>
            <div className="ab-compat-page-hero-image-wrap">
              <HeroImage product={displaySide.product} shortLabel={config.displayShortLabel} />
            </div>
            <p className="ab-compat-page-hero-name">
              {displaySide.product?.name || config.displayShortLabel}
            </p>
            {displaySide.productError ? (
              <p className="ab-compat-page-error" data-testid="product-error-display">
                {config.displayShortLabel} details could not be loaded from the product catalog.
              </p>
            ) : null}
          </div>
          <div
            className="ab-compat-page-hero-card"
            data-testid="compatibility-hero-livescope"
            data-canonical-slug={config.livescopeSlug}
          >
            <p className="ab-compat-page-hero-label">{config.livescopeShortLabel}</p>
            <div className="ab-compat-page-hero-image-wrap">
              <HeroImage product={livescopeSide.product} shortLabel={config.livescopeShortLabel} />
            </div>
            <p className="ab-compat-page-hero-name">
              {livescopeSide.product?.name || config.livescopeShortLabel}
            </p>
            {livescopeSide.productError ? (
              <p className="ab-compat-page-error" data-testid="product-error-livescope">
                {config.livescopeShortLabel} details could not be loaded from the product catalog.
              </p>
            ) : null}
          </div>
        </div>
      </header>

      <section className="ab-compat-page-section" data-testid="compatibility-result-section">
        <CompatibilityResult
          viewModel={viewModel}
          includeIceCompleteness={false}
        />
      </section>

      {connectionFacts.length ? (
        <section className="ab-compat-page-section" data-testid="compatibility-how-it-connects">
          <h2 className="ab-compat-page-section-title">How it connects</h2>
          <div className="ab-compat-page-card">
            <ul className="ab-compat-page-facts">
              {connectionFacts.map((fact) => (
                <li key={fact}>{fact}</li>
              ))}
            </ul>
          </div>
        </section>
      ) : null}

      <section
        ref={offersRef}
        className="ab-compat-page-section"
        data-testid="compatibility-current-offers"
      >
        <h2 className="ab-compat-page-section-title">Current retailer offers</h2>
        <div className="ab-compat-page-commerce-grid">
          <div className="ab-compat-page-card">
            <h3 className="ab-compat-page-hero-label">{config.displayShortLabel}</h3>
            <OfferList
              sideKey="display"
              shortLabel={config.displayShortLabel}
              emptyOffersMessage={config.emptyOffersMessageDisplay}
              commerceIntro={`If you need the ${config.displayShortLabel}, current verified offers are below.`}
              side={displaySide}
              productSlug={config.displaySlug}
              config={config}
            />
          </div>
          <div className="ab-compat-page-card">
            <h3 className="ab-compat-page-hero-label">{config.livescopeShortLabel}</h3>
            <OfferList
              sideKey="livescope"
              shortLabel={config.livescopeShortLabel}
              emptyOffersMessage={config.emptyOffersMessageLivescope}
              commerceIntro={`If you need the ${config.livescopeShortLabel}, current verified offers are below.`}
              side={livescopeSide}
              productSlug={config.livescopeSlug}
              config={config}
            />
          </div>
        </div>
      </section>

      {config.relatedLinks?.length ? (
        <section className="ab-compat-page-section" data-testid="compatibility-next-steps">
          <h2 className="ab-compat-page-section-title">Next step</h2>
          <div className="ab-compat-page-next">
            {config.relatedLinks.map((link) => (
              <a
                key={link.href}
                className="ab-compat-page-next-link"
                href={link.href}
              >
                {link.label}
              </a>
            ))}
          </div>
        </section>
      ) : null}
    </article>
  );
}
